import z from 'zod';
import { AssetTypeKey } from './asset-config';

export interface MapLayerConfig {
  id: string;
  name: string;
  slug: AssetTypeKey | string; // TODO: map to AssetTypeKey
  geomType?: string; // e.g. 'point', 'line', 'polygon'
  category?: string; // e.g. 'Service Work', 'Utility'
  layerType: LayerTypes | string; // e.g. 'asset', 'ticket', 'field-note'
  styles?: Record<string, any>;
  description?: string;
  zIndex?: number;
  opacity?: number;
  isVisible: boolean;
  minZoom?: number | null;
  maxZoom?: number | null;
  primaryDisplayField?: string | null;
  searchFields?: string[];
}

export interface LayerTypes {
  PREMISE: 'premise';
  SERVICE_AREA: 'service-area';
  ASSET: 'asset';
  TICKET: 'ticket';
  FIELD_NOTE: 'field-note';
}

const ColorSchema = z.string().regex(/^#/, 'Must be a hex color'); // e.g. #ff0000
const LineDashSchema = z.array(z.number()); // e.g. [10, 10] for dashed

export const LayerStyleSchema = z.object({
  // --- General Geometry Styling ---
  color: ColorSchema.default('#3b82f6'), // Primary color (Fill for points, Stroke for lines)
  opacity: z.number().min(0).max(1).default(1),

  // --- Point Specific ---
  pointType: z.enum(['circle', 'icon', 'square']).default('circle'),
  pointSize: z.number().min(1).default(6), // Radius for circle, scale for icon
  iconUrl: z.string().url().optional(), // For specific utility assets (e.g. Transformer icon)
  iconRotation: z.number().default(0), // Degrees to rotate icon

  // --- Line/Stroke Specific ---
  lineWidth: z.number().min(0).default(2),
  lineDash: LineDashSchema.optional(), // Essential for "Proposed" vs "Existing" utility lines
  strokeColor: ColorSchema.optional(), // Override if you want stroke diff from fill

  // --- Polygon Specific ---
  fillColor: ColorSchema.optional(), // Often usually separate from stroke color (e.g., transparent fill)

  // --- Labeling ---
  showLabel: z.boolean().default(false),
  labelField: z.string().default('structureNo'), // Which DB field to use?
  labelFontSize: z.number().default(12),
  labelColor: ColorSchema.default('#1f2937'),
  labelOffset: z.number().default(-15),
});

export type LayerStyleConfig = z.infer<typeof LayerStyleSchema>;

export const DEFAULT_LAYER_STYLE: LayerStyleConfig = {
  color: '#64748b',
  opacity: 1,
  pointType: 'circle',
  pointSize: 6,
  lineWidth: 2,
  showLabel: false,
  labelField: 'name',
  labelFontSize: 12,
  labelColor: '#000000',
  labelOffset: -12,
  iconRotation: 0,
  strokeColor: '#ffffff',
  fillColor: '#64748b',
  iconUrl: undefined,
  lineDash: undefined,
};
